import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getFormTemplateDocumentRequest,
  getFormTemplateDocumentSuccess,
  getPhotoLayoutsRequest,
} from "../../../../../redux/form-templates/slice";
import Loading from "../../../../../components/loading/loading";
import ExcelIcon from "../../../../../assets/Excel_Icon.svg";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import UploadDocument from "./upload-document";
import "./excel-file.css";
import {
  getFormDocumentSuccess,
  getFormDocumentRequest,
} from "../../../../../redux/clients/slice";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  downloadTemporaryFileRequest,
  updateFormPhotoOptionsRequest,
  updateFormTemplatePhotoOptionsRequest,
} from "../../../../../utils/APICalls";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import { userType } from "../../../../../utils/Constant";

dayjs.extend(utc);

type FormValues = {
  photoDestination: string;
  photoSheet: string;
  photoLayout: string;
};

const ValidationSchema = yup.object({
  photoDestination: yup.string().required("Please select any one option"),
  photoSheet: yup.string().when("photoDestination", {
    is: (value: string) => value === "none",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("This field can't be empty"),
  }),
  photoLayout: yup.string().when("photoDestination", {
    is: (value: string) => value === "none",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("This field can't be empty"),
  }),
});

export default function ExcelFile({ data }: any) {
  const dispatch = useDispatch();

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [documentDetails, setDocumentDetails] = useState<any>();
  const [isPhotoOptionsEdit, setIsPhotoOptionsEdit] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, formTemplateDocument, photoLayouts } = useSelector(
    (state: any) => state.formTemplates
  );
  const { loading: clientLoading, formDocument } = useSelector(
    (state: any) => state.clients
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        // Client Form
        dispatch(getFormDocumentSuccess({ response: null }));
        dispatch(getFormDocumentRequest({ id: data?.id }));
      } else {
        // Form Template
        dispatch(getFormTemplateDocumentSuccess({ response: null }));
        dispatch(getFormTemplateDocumentRequest({ id: data?.id }));
      }
      dispatch(getPhotoLayoutsRequest());
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        setDocumentDetails(formDocument);
      } else {
        setDocumentDetails(formTemplateDocument);
      }
    }
  }, [data, formTemplateDocument, formDocument]);

  useEffect(() => {
    if (documentDetails) {
      formik.setFieldValue(
        "photoDestination",
        documentDetails?.photoSheetName && "sheet"
      );
      formik.setFieldValue("photoSheet", documentDetails?.photoSheetName);
      formik.setFieldValue(
        "photoLayout",
        documentDetails?.photoLayout?.id || ""
      );
    }
  }, [documentDetails]); // eslint-disable-line

  const formik = useFormik<FormValues>({
    initialValues: {
      photoDestination: "",
      photoSheet: "",
      photoLayout: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload: any = {};
      if (values.photoDestination === "sheet") {
        payload.photoLayoutId = values.photoLayout;
        payload.photoSheetName = values.photoSheet;
      }
      savePhotoOptions(payload);
    },
  });

  const savePhotoOptions = async (payload: any) => {
    setShowLoader(true);
    let response;
    if (data?.clientId) {
      response = await updateFormPhotoOptionsRequest(data?.id, payload);
    } else {
      response = await updateFormTemplatePhotoOptionsRequest(data?.id, payload);
    }
    setShowLoader(false);
    if (response?.status === 204) {
      formik.resetForm();
      if (data?.clientId) {
        dispatch(getFormDocumentRequest({ id: data?.id }));
      } else {
        dispatch(getFormTemplateDocumentRequest({ id: data?.id }));
      }
      setIsPhotoOptionsEdit(false);
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response.data?.detail);
    }
  };

  const onDownloadClick = async () => {
    const documentKey = documentDetails?.fileKey;
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(documentKey);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = documentDetails?.fileName;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  const getFormattedDate = (date: string) => {
    return date
      ? `${dayjs.utc(date).format("MMM D, YYYY")} at ${dayjs
          .utc(date)
          .format("hh:mma")} MDT`
      : "-";
  };

  return (
    <>
      <Card className="card-layout">
        <Typography>
          If the output of this form is excel, upload the final excel file from
          the client. The system will scan the file and find all cells with
          <b style={{ marginLeft: 5 }}>defined names</b>. These are unique names
          given to cells within the Excel file where you want answers to
          populate. This allows you to build an HTML form, and have the response
          from the HTML form populate the excel file that is delivered to the
          client.
        </Typography>
        <Box className="display-name-layout">
          <Typography className="label-txt">Excel File</Typography>
          {documentDetails ? (
            <Box className="document-data-layout">
              <Box className="document-data-content">
                <Box display={"flex"}>
                  <img
                    src={ExcelIcon}
                    alt="Excel-icon"
                    height={40}
                    width={40}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography>{documentDetails?.fileName}</Typography>
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
                    >
                      Complete
                    </Typography>
                  </Box>
                </Box>
                {data?.clientId ? (
                  [
                    currentUser?.userType === userType?.tenant ? (
                      <Box display={"flex"}>
                        <Button
                          sx={{ ml: 3 }}
                          variant="outlined"
                          startIcon={<DownloadOutlinedIcon />}
                          onClick={onDownloadClick}
                        >
                          Download
                        </Button>
                        <Button
                          sx={{ ml: 3 }}
                          variant="contained"
                          startIcon={<UploadOutlinedIcon />}
                          onClick={() => setShowReplaceDialog(true)}
                        >
                          Replace
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        sx={{ ml: 3 }}
                        variant="outlined"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={onDownloadClick}
                      >
                        Download
                      </Button>
                    ),
                  ]
                ) : (
                  <Box display={"flex"}>
                    <Button
                      variant="text"
                      startIcon={<DeleteOutlinedIcon />}
                      color="error"
                      disabled
                    >
                      Remove
                    </Button>
                    <Button
                      sx={{ ml: 3 }}
                      variant="outlined"
                      startIcon={<DownloadOutlinedIcon />}
                      onClick={onDownloadClick}
                    >
                      Download
                    </Button>
                    <Button
                      sx={{ ml: 3 }}
                      variant="contained"
                      startIcon={<UploadOutlinedIcon />}
                      onClick={() => setShowReplaceDialog(true)}
                    >
                      Replace
                    </Button>
                  </Box>
                )}
              </Box>
              <Box display={"flex"} mt={1.5}>
                <Typography className="document-uploaded-txt">
                  Uploaded
                </Typography>
                <Typography fontSize={14} fontStyle={"italic"}>
                  {getFormattedDate(documentDetails?.uploadedDateTime)}
                </Typography>
              </Box>
              <Box display={"flex"}>
                <Typography className="document-uploaded-txt">
                  Uploaded By
                </Typography>
                <Typography fontSize={14} fontStyle={"italic"}>
                  {documentDetails?.uploadedBy}
                </Typography>
              </Box>
              <Box display={"flex"}>
                <Typography className="document-uploaded-txt">
                  Defined Names
                </Typography>
                <Typography fontSize={14} fontStyle={"italic"}>
                  {documentDetails?.definedNamesCount}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<UploadOutlinedIcon />}
              onClick={() => setShowUploadDialog(true)}
            >
              Upload File
            </Button>
          )}
        </Box>

        <Box className="photo-options-layout">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography fontSize={24}>Photo Options</Typography>
            <Button
              variant="text"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setIsPhotoOptionsEdit(true)}
            >
              Edit
            </Button>
          </Box>
          {isPhotoOptionsEdit ? (
            <form onSubmit={formik.handleSubmit}>
              <Box mt={2}>
                <Typography sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 12 }}>
                  Photo Destination
                </Typography>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.photoDestination &&
                    Boolean(formik.errors.photoDestination)
                  }
                >
                  <RadioGroup
                    sx={{ ml: 1 }}
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="photoDestination"
                    value={formik.values.photoDestination}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value="sheet"
                      control={<Radio />}
                      label="Selected Tab of Spreadsheet"
                    />
                    {formik.values.photoDestination === "sheet" && (
                      <FormControl
                        sx={{ ml: 4, mt: 0.5 }}
                        error={
                          formik.touched.photoSheet &&
                          Boolean(formik.errors.photoSheet)
                        }
                      >
                        <InputLabel id="photo-layout-select-label">
                          Select Tab
                        </InputLabel>
                        <Select
                          labelId="photo-sheet-select-label"
                          id="photoSheet"
                          name="photoSheet"
                          value={formik.values.photoSheet}
                          label="Select Tab"
                          onChange={formik.handleChange}
                        >
                          {documentDetails?.sheetNames.map((sheet: string) => (
                            <MenuItem value={sheet}>{sheet}</MenuItem>
                          ))}
                        </Select>
                        {Boolean(formik.errors.photoSheet) &&
                        formik.touched.photoSheet ? (
                          <FormHelperText>
                            {formik.errors.photoSheet}
                          </FormHelperText>
                        ) : (
                          <FormHelperText>
                            Photos will be automatically inserted on the
                            selected page of the spreadsheet. If the desired
                            destination does not exist, edit the spread sheet
                            and re-upload.
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                    <FormControlLabel
                      value="none"
                      control={<Radio />}
                      label="None (Photos Must be Added by Modifying Form Prior to Completion)"
                    />
                  </RadioGroup>
                  {Boolean(formik.errors.photoDestination) &&
                    formik.touched.photoDestination && (
                      <FormHelperText>
                        {formik.errors.photoDestination}
                      </FormHelperText>
                    )}
                </FormControl>
              </Box>
              {formik.values.photoDestination !== "none" && (
                <FormControl
                  fullWidth
                  sx={{ mt: 2 }}
                  error={
                    formik.touched.photoLayout &&
                    Boolean(formik.errors.photoLayout)
                  }
                >
                  <InputLabel id="photo-layout-select-label">
                    Photo Layout
                  </InputLabel>
                  <Select
                    labelId="photo-layout-select-label"
                    id="photoLayout"
                    name="photoLayout"
                    value={formik.values.photoLayout}
                    label="Photo Layout"
                    onChange={formik.handleChange}
                  >
                    {photoLayouts?.map((layout: any) => (
                      <MenuItem value={layout?.id}>
                        {layout?.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.photoLayout) &&
                    formik.touched.photoLayout && (
                      <FormHelperText>
                        {formik.errors.photoLayout}
                      </FormHelperText>
                    )}
                </FormControl>
              )}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  sx={{ mr: 2, fontWeight: 510 }}
                  variant="outlined"
                  onClick={() => {
                    if (!documentDetails?.photoSheetName) {
                      // if saved data is available, don't reset the form
                      formik.resetForm();
                    }
                    setIsPhotoOptionsEdit(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  sx={{ fontWeight: 510 }}
                  variant="contained"
                  onClick={() => formik.handleSubmit()}
                >
                  Save Changes
                </Button>
              </Box>
            </form>
          ) : (
            <>
              <Box mt={2}>
                <Typography sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 12 }}>
                  Photo Destination
                </Typography>
                <Typography>
                  {documentDetails?.photoSheetName || "-"}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 12 }}>
                  Photo Layout
                </Typography>
                <Typography>
                  {documentDetails?.photoLayout?.displayName || "-"}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Card>

      {showUploadDialog && (
        <UploadDocument
          showDialog={showUploadDialog}
          closeDialog={() => setShowUploadDialog(false)}
          templateId={data?.id}
          isClientForm={!!data?.clientId}
        />
      )}

      {showReplaceDialog && (
        <UploadDocument
          showDialog={showReplaceDialog}
          isReplace={true}
          closeDialog={() => setShowReplaceDialog(false)}
          templateId={data?.id}
          isClientForm={!!data?.clientId}
        />
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      {(loading || clientLoading || showLoader) && <Loading />}
    </>
  );
}
